import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';

import { EmployeesDetailComponent } from '../../employees/employees-detail/employees-detail.component';
import { EmployeeState } from '../../employees/employees.state';
import { Employee } from '../../models/Employee';
import { ObjectWithFinancialData } from '../../models/Financial/ObjectWithFinancialData';
import { DateNavigationOptions, DateNavigationType, SelectedDateClass } from '../../shared/date-navigation/date-navigation.component';
import { GetEmployeesFinancialData } from '../financial.actions';
import { FinancialService } from '../financial.service';
import { FinancialState } from '../financial.state';

@Component({
    selector: 'app-employee-financial-graphs',
    templateUrl: './employee-financial-graphs.component.html',
    styleUrls: ['./employee-financial-graphs.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EmployeeFinancialGraphsComponent implements OnInit, OnDestroy {
    @Select(EmployeeState.Employees) allEmployees$: Observable<Employee[]>;

    employeesWithFinancialData: ObjectWithFinancialData[];
    datasource: MatTableDataSource<ObjectWithFinancialData>;
    displayedColumns: string[] = ['employee', 'margin', 'marginTotal', 'totalIncome', 'totalCosts'];

    selectedDate: Date = new Date();
    dateNavigationOptions: DateNavigationOptions;
    fetchedDataForCurrentYear = false;
    loadingData = false;
    showArchived = false;
    searchText = '';
    aiAnalyzing = false;
    aiAnalysisHtml: SafeHtml = null;
    sendingEmail = false;
    private allEmployees: Employee[] = [];

    private sSort: MatSort;
    private subscriptions = new Subscription();

    constructor(
        private store: Store,
        private modalService: NgbModal,
        private financialService: FinancialService,
        private sanitizer: DomSanitizer,
        private toastr: ToastrService,
    ) {}

    @ViewChild('sort') set sort(ms: MatSort) {
        this.sSort = ms;
        this.setDataSourceAttributes();
    }

    setDataSourceAttributes(): void {
        this.datasource.sort = this.sSort;
    }

    ngOnInit(): void {
        this.dateNavigationOptions = {
            dateNavigationType: DateNavigationType.YEAR,
            selectedDate: this.selectedDate,
            selectedDateClass: SelectedDateClass.SMALL,
        };

        // Subscribe to all employees for search functionality
        this.subscriptions.add(
            this.allEmployees$.subscribe((employees) => {
                this.allEmployees = employees || [];
            }),
        );

        this.createMatTable();
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openEmployeeDetailModal(employeeWithFinancialData: ObjectWithFinancialData): void {
        const modalRef = this.modalService.open(EmployeesDetailComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });
        modalRef.componentInstance.employeeUuid = employeeWithFinancialData.objectId;
        modalRef.componentInstance.openFinancialTab = true;
    }

    updateSelectedDate(): void {
        this.fetchedDataForCurrentYear = false;
        this.fetchData();
    }

    filterData() {
        this.applyCurrentFilter();
    }

    applyFilter(event: Event): void {
        this.searchText = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.applyCurrentFilter();
    }

    // Summary helpers
    getActiveEmployeeCount(): number {
        return this.datasource?.filteredData?.length || 0;
    }

    getFreelanceCount(): number {
        if (!this.datasource?.filteredData) return 0;
        return this.datasource.filteredData.filter((d) => {
            const emp = this.allEmployees.find((e) => e.uuid === d.objectId);
            return emp?.type === 'FREELANCE';
        }).length;
    }

    getPayrollCount(): number {
        if (!this.datasource?.filteredData) return 0;
        return this.datasource.filteredData.filter((d) => {
            const emp = this.allEmployees.find((e) => e.uuid === d.objectId);
            return !emp?.type || emp.type === 'PAYROLL';
        }).length;
    }

    getAvgMargin(): number {
        if (!this.datasource?.filteredData) return 0;
        const withData = this.datasource.filteredData.filter((d) => d.totalIncome > 0 || d.totalCosts > 0);
        if (withData.length === 0) return 0;
        const sum = withData.reduce((acc, d) => acc + this.getMarginPercent(d), 0);
        return sum / withData.length;
    }

    getTotalIncome(): number {
        if (!this.datasource?.filteredData) return 0;
        return this.datasource.filteredData.reduce((acc, d) => acc + (d.totalIncome || 0), 0);
    }

    getTotalCosts(): number {
        if (!this.datasource?.filteredData) return 0;
        return this.datasource.filteredData.reduce((acc, d) => acc + (d.totalCosts || 0), 0);
    }

    getTotalMarginAmount(): number {
        return this.getTotalIncome() - this.getTotalCosts();
    }

    // Employee type helpers
    getEmployeeTypeLabel(objectId: string): string {
        const emp = this.allEmployees.find((e) => e.uuid === objectId);
        return emp?.type === 'FREELANCE' ? 'Freelance' : 'Payroll';
    }

    getEmployeeTypeBadgeClass(objectId: string): string {
        const emp = this.allEmployees.find((e) => e.uuid === objectId);
        return emp?.type === 'FREELANCE' ? 'type-badge--freelance' : 'type-badge--payroll';
    }

    // Margin styling helpers
    getMarginPercent(row: ObjectWithFinancialData): number {
        const income = row.totalIncome || 0;
        const costs = row.totalCosts || 0;
        const margin = income - costs;
        if (margin === 0) return 0;
        if (income > 0) return (margin / income) * 100;
        if (costs > 0) return (margin / costs) * 100;
        return margin < 0 ? -100 : 0;
    }

    getMarginClass(row: ObjectWithFinancialData): string {
        const pct = this.getMarginPercent(row);
        if (pct >= 30) return 'margin-value--high';
        if (pct >= 15) return 'margin-value--medium';
        return 'margin-value--low';
    }

    // Tooltip helpers
    private eur(value: number): string {
        return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
    }

    private eurRate(value: number): string {
        return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    }

    getCostsTooltip(row: ObjectWithFinancialData): string {
        const hours = row.totalHoursWorked || 0;
        const costs = row.totalCosts || 0;
        const budgetRate = row.budgetedHourlyRate || 0;
        const lines: string[] = [];
        lines.push(`Gepresteerde uren: ${hours.toFixed(1)}h`);
        if (budgetRate > 0) {
            lines.push(`Budget uurprijs: ${this.eurRate(budgetRate)}/u`);
        }
        if (hours > 0 && costs > 0) {
            lines.push(`Effectief kost/uur: ${this.eurRate(costs / hours)}/u`);
        }
        if (row.hoursBreakdown?.length) {
            lines.push('');
            lines.push('Per project:');
            row.hoursBreakdown.forEach((h) => lines.push('• ' + h));
        }
        return lines.join('\n');
    }

    getIncomeTooltip(row: ObjectWithFinancialData): string {
        const hours = row.totalHoursWorked || 0;
        const income = row.totalIncome || 0;
        const lines: string[] = [];
        lines.push(`Totaal: ${this.eur(income)}`);
        if (hours > 0) {
            lines.push(`Gem. opbrengst/uur: ${this.eurRate(income / hours)}/u`);
        }
        if (row.incomeBreakdown?.length) {
            lines.push('');
            lines.push('Per project/klant:');
            row.incomeBreakdown.forEach((i) => lines.push('• ' + i));
        }
        return lines.join('\n');
    }

    getMarginTooltip(row: ObjectWithFinancialData): string {
        const income = row.totalIncome || 0;
        const costs = row.totalCosts || 0;
        const margin = income - costs;
        const hours = row.totalHoursWorked || 0;
        const lines: string[] = [];
        lines.push(`Inkomen: ${this.eur(income)}`);
        lines.push(`Kosten: ${this.eur(costs)}`);
        lines.push(`Marge: ${this.eur(margin)} (${this.getMarginPercent(row).toFixed(0)}%)`);
        if (hours > 0) {
            lines.push(`Marge/uur: ${this.eurRate(margin / hours)}/u`);
        }
        return lines.join('\n');
    }

    // AI Analysis
    runAiAnalysis(): void {
        this.aiAnalyzing = true;
        const year = this.dateNavigationOptions.selectedDate.getFullYear();

        const data = this.datasource.filteredData.map((row) => {
            const emp = this.allEmployees.find((e) => e.uuid === row.objectId);
            return {
                name: emp ? `${emp.firstName} ${emp.lastName}`.trim() : 'Unknown',
                type: emp?.type || 'PAYROLL',
                totalIncome: Math.round(row.totalIncome),
                totalCosts: Math.round(row.totalCosts),
                marginEur: Math.round(row.totalIncome - row.totalCosts),
                marginPct: Math.round(this.getMarginPercent(row)),
            };
        });

        this.financialService.analyzeEmployeeFinancials(year, data).subscribe({
            next: (res) => {
                this.aiAnalysisHtml = this.sanitizer.bypassSecurityTrustHtml(this.formatAnalysis(res.analysis));
                this.aiAnalyzing = false;
            },
            error: () => {
                this.toastr.error('Er ging iets mis bij de AI analyse.');
                this.aiAnalyzing = false;
            },
        });
    }

    // Send Email
    sendEmail(): void {
        const email = prompt('Enter email address:');
        if (!email) return;
        this.sendingEmail = true;
        const year = this.dateNavigationOptions.selectedDate.getFullYear();
        this.financialService.sendEmployeeFinancialEmail(year, email).subscribe({
            next: () => {
                this.toastr.success('Email sent!');
                this.sendingEmail = false;
            },
            error: () => {
                this.toastr.error('Failed to send email.');
                this.sendingEmail = false;
            },
        });
    }

    private formatAnalysis(text: string): string {
        if (!text) {
            return '';
        }

        const normalized = text.replace(/\r\n/g, '\n').trim();
        if (!normalized) {
            return '';
        }

        const sections = this.extractAnalysisSections(normalized);
        if (!sections.length) {
            return `<section class="ai-section"><div class="ai-section__body">${this.formatSectionBody('', normalized)}</div></section>`;
        }

        return sections
            .map(
                (section) =>
                    `<section class="ai-section">` +
                    `<h5 class="ai-section__title">${this.escapeHtml(section.title)}</h5>` +
                    `<div class="ai-section__body">${this.formatSectionBody(section.title, section.content)}</div>` +
                    `</section>`,
            )
            .join('');
    }

    private extractAnalysisSections(text: string): Array<{ title: string; content: string }> {
        const sectionPattern = /^(SAMENVATTING|AANDACHTSPUNTEN|ACTIEPUNTEN|DETAILS|CONCLUSIE)\s*:?\s*$/gim;
        const matches: Array<RegExpExecArray & { index: number }> = [];
        let m: RegExpExecArray | null;
        // eslint-disable-next-line no-cond-assign
        while ((m = sectionPattern.exec(text)) !== null) {
            matches.push(m as RegExpExecArray & { index: number });
        }

        if (!matches.length) {
            return [];
        }

        const sections: Array<{ title: string; content: string }> = [];
        const firstMatch = matches[0];
        if (firstMatch.index > 0) {
            const intro = text.slice(0, firstMatch.index).trim();
            if (intro) {
                sections.push({ title: 'OVERZICHT', content: intro });
            }
        }

        matches.forEach((match, index) => {
            const start = match.index + match[0].length;
            const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
            const content = text.slice(start, end).trim();
            sections.push({ title: (match[1] || '').toUpperCase(), content });
        });

        return sections.filter((section) => !!section.content);
    }

    private formatSectionBody(sectionTitle: string, rawText: string): string {
        const clean = rawText.trim();
        if (!clean) {
            return '<p>Geen inhoud beschikbaar.</p>';
        }

        const plainLines = clean
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => !!line);

        const forceList = ['AANDACHTSPUNTEN', 'ACTIEPUNTEN'].includes(sectionTitle.toUpperCase());
        if (forceList) {
            return this.renderList(plainLines);
        }

        const paragraphs = clean
            .split(/\n{2,}/)
            .map((chunk) => chunk.trim())
            .filter((chunk) => !!chunk);

        return paragraphs
            .map((paragraph) => {
                const paragraphLines = paragraph
                    .split('\n')
                    .map((line) => line.trim())
                    .filter((line) => !!line);

                const isListBlock = paragraphLines.length > 0 && paragraphLines.every((line) => /^([-*•]|\d+[.)])\s+/.test(line));

                if (isListBlock) {
                    return this.renderList(paragraphLines);
                }

                const merged = paragraphLines.join(' ');
                return `<p>${this.formatInline(merged)}</p>`;
            })
            .join('');
    }

    private renderList(lines: string[]): string {
        if (!lines.length) {
            return '';
        }

        const items = lines
            .map((line) => line.replace(/^([-*•]|\d+[.)])\s+/, '').trim())
            .filter((line) => !!line)
            .map((line) => `<li>${this.formatInline(line)}</li>`)
            .join('');

        return items ? `<ul class="ai-list">${items}</ul>` : '';
    }

    private formatInline(text: string): string {
        return this.escapeHtml(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    }

    private escapeHtml(text: string): string {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    private applyCurrentFilter(): void {
        // Trigger filter re-evaluation by setting a unique string (actual logic is in filterPredicate)
        this.datasource.filter = `${this.showArchived}|${this.searchText}|${Date.now()}`;
    }

    private fetchData(): void {
        const year: number = this.dateNavigationOptions.selectedDate.getFullYear();

        this.subscriptions.add(
            this.store.select(FinancialState.employeesFinancialDataForYear(year)).subscribe((employeesFinancialData) => {
                if (!this.fetchedDataForCurrentYear && (!employeesFinancialData || employeesFinancialData.length < 1)) {
                    this.fetchedDataForCurrentYear = true;
                    this.loadingData = true;

                    this.subscriptions.add(
                        this.store.dispatch(new GetEmployeesFinancialData(year)).subscribe({
                            error: () => {
                                this.loadingData = false;
                            },
                            complete: () => {
                                this.loadingData = false;
                            },
                        }),
                    );
                }

                if (employeesFinancialData && employeesFinancialData.length > 0) {
                    this.loadingData = false;
                    this.employeesWithFinancialData = employeesFinancialData;
                    this.datasource.data = employeesFinancialData;
                    this.datasource.sort = this.sSort;

                    this.datasource.sortingDataAccessor = (item: ObjectWithFinancialData, property: string) => {
                        switch (property) {
                            case 'employee':
                                const employee = this.allEmployees.find((e) => e.uuid === item.objectId);
                                const fullName = employee ? `${employee.firstName} ${employee.lastName}`.trim().toLowerCase() : '';
                                return fullName;
                            case 'margin':
                                return this.getMarginPercent(item);
                            case 'marginTotal':
                                return item.totalIncome - item.totalCosts;
                            case 'totalCosts':
                                return item.totalCosts || 0;
                            case 'totalIncome':
                                return item.totalIncome || 0;
                            default:
                                return (item as any)[property];
                        }
                    };

                    this.datasource.filterPredicate = (data: ObjectWithFinancialData, _filter: string) => {
                        if (!this.showArchived && data.archived) {
                            return false;
                        }
                        if (!this.searchText) {
                            return true;
                        }
                        const employee = this.allEmployees.find((e) => e.uuid === data.objectId);
                        const firstName = employee?.firstName ? employee.firstName.toLowerCase() : '';
                        const lastName = employee?.lastName ? employee.lastName.toLowerCase() : '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        return fullName.includes(this.searchText);
                    };

                    this.applyCurrentFilter();
                } else {
                    this.employeesWithFinancialData = [];
                    this.datasource.data = [];
                }
            }),
        );
    }

    private createMatTable(): void {
        this.datasource = new MatTableDataSource<ObjectWithFinancialData>();
    }
}
